import { Settings, User, LayoutDashboard, Cookie, Plus, Info } from "lucide-react";
import { SettingsMenu } from "./SettingsMenu";
import { AuthButton } from "./auth/AuthButton";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const Header = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [biscuitsCount, setBiscuitsCount] = useState(0);
  const [topUpAmount, setTopUpAmount] = useState(20);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);

      if (session) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_superuser, biscuits_count')
          .eq('id', session.user.id)
          .single();

        if (profile) {
          setIsSuperAdmin(profile.is_superuser);
          setBiscuitsCount(profile.biscuits_count || 0);
        }
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
      if (!session) {
        setIsSuperAdmin(false);
        setBiscuitsCount(0);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleTopUp = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    if (topUpAmount < 20) {
      toast.error("Minimum top-up amount is $10 (20 cookies)");
      return;
    }

    try {
      const { error: topUpError } = await supabase
        .from('cookie_top_ups')
        .insert({
          user_id: session.user.id,
          amount: topUpAmount
        });

      if (topUpError) throw topUpError;

      const { data: profile, error: updateError } = await supabase
        .from('profiles')
        .update({
          biscuits_count: biscuitsCount + topUpAmount
        })
        .eq('id', session.user.id)
        .select('biscuits_count')
        .single();

      if (updateError) throw updateError;

      if (profile) {
        setBiscuitsCount(profile.biscuits_count || 0);
        toast.success(`Added ${topUpAmount} cookies ($${topUpAmount * 0.5}) to your Cookie Jar!`);
      }
    } catch (error) {
      console.error('Error topping up cookies:', error);
      toast.error('Failed to top up your Cookie Jar');
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 0;
    setTopUpAmount(value);
  };

  const cookieJarButton = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Cookie className="w-5 h-5" />
          <span className="absolute -top-1 -right-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {biscuitsCount}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="flex items-center justify-between px-2">
          <DropdownMenuLabel>Cookie Jar</DropdownMenuLabel>
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="bg-white p-2 z-[100]">
                <p className="max-w-xs text-sm">
                  Cookies are fun in-platform tokens you can use to tip and appreciate creators. 
                  Show your support by sharing cookies with content you love!
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <DropdownMenuSeparator />
        <div className="p-2">
          <div className="text-center mb-2">
            <span className="text-2xl font-bold">{biscuitsCount}</span>
            <p className="text-sm text-muted-foreground">Biscuits Available</p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => setTopUpAmount(Math.max(20, topUpAmount - 2))}
                className="h-8 w-12"
              >
                -
              </Button>
              <Input
                type="number"
                min="20"
                step="2"
                value={topUpAmount}
                onChange={handleAmountChange}
                className="w-full text-center h-8 px-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => setTopUpAmount(topUpAmount + 2)}
                className="h-8 w-12"
              >
                +
              </Button>
            </div>
            <Button 
              className="w-full" 
              onClick={handleTopUp}
            >
              <Plus className="mr-2 h-4 w-4" /> Top-up ${topUpAmount * 0.5}
            </Button>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header className="relative w-full py-6 bg-white border-b border-gray-100">
      <div className="container max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="w-full md:w-auto text-center md:text-left">
            <Link to="/" className="hover:opacity-90 transition-opacity inline-block">
              <img 
                src="/lovable-uploads/477186b7-9af3-4bec-a238-82b021f153b9.png" 
                alt="Airfryer AI" 
                className="h-12 md:h-14 translate-y-[3px]"
              />
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <div className="md:hidden flex items-center gap-4">
              {isSuperAdmin && cookieJarButton}
              <AuthButton />
              <SettingsMenu />
            </div>
            <div className="hidden md:flex items-center gap-6">
              {isSuperAdmin && cookieJarButton}
              <AuthButton />
              <SettingsMenu />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
